'use client';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FormControl, FormDescription, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Maximize, MessageSquare, Minimize, Save, Send, WandSparkles } from 'lucide-react';
import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ChatMessages, { type ChatMessage } from '../components/ChatMessages';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import { useSubscription } from '../hooks/useSubscription';
import type { ReviewDocRequest, ReviewDocResponse, StatusResponse } from '../types/api';
import type { EditDocumentRequest, EditDocumentResponse } from '../types/document';

const Editor = React.lazy(() => import('@/components/Editor'));

const POLLING_INTERVAL = 2000; // Reduced polling interval for better responsiveness
const MAX_POLLING_ATTEMPTS = 90; // 3 minutes at 2-second intervals

// Local storage key for reference IDs
const getWorkingKey = (id: string) => `working_${id}`;

// Functions for managing reference IDs in local storage
const setReferenceId = (reference: string, id: string) => {
  localStorage.setItem(getWorkingKey(id), reference);
};

const getReferenceId = (id: string) => {
  return localStorage.getItem(getWorkingKey(id));
};

const removeReferenceId = (id: string) => {
  localStorage.removeItem(getWorkingKey(id));
};

const EditDocument: React.FC = () => {
  // Document state
  const [document, setDocument] = useState<EditDocumentResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [isReviewing, setIsReviewing] = useState(false);
  const [isQuickRating, setIsQuickRating] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isChatMaximized, setIsChatMaximized] = useState(false);

  // Error and message state
  const [error, setError] = useState<Error | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [reviewMessage, setReviewMessage] = useState<string | null>(null);

  // Chat state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');

  // Credit state
  const [creditError, setCreditError] = useState<string | null>(null);
  const [creditDelta, setCreditDelta] = useState<number | null>(null);

  // Hooks and refs
  const { setSubscriptionSpend, setSubscriptionLimit } = useSubscription();
  const { apiCall } = useAuthenticatedApi();
  const location = useLocation();
  const navigate = useNavigate();
  const editorDataRef = useRef('');
  const { toast } = useToast();
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Form setup
  const form = useForm({
    defaultValues: {
      context: document?.context || 'fiction',
      complete: document?.docOptions?.complete || false,
    },
  });

  // Get document ID from URL
  const id = new URLSearchParams(location.search).get('id');

  // Genre options
  const contextOptions = [
    { value: 'fiction', label: 'Fiction', description: 'A made-up story' },
    { value: 'non-fiction', label: 'Non-Fiction', description: 'A true story' },
    { value: 'movie-script', label: 'Movie Script', description: 'A movie script' },
  ];

  // Helper function to add a system message to the chat
  const addSystemMessage = useCallback((message: string) => {
    setChatMessages((prev) => [
      ...prev,
      {
        user: false,
        system: true,
        msg: message,
        created: Math.floor(Date.now() / 1000),
      },
    ]);
  }, []);

  // Clean up polling timer on unmount
  useEffect(() => {
    return () => {
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
      }
    };
  }, []);

  // Poll for status of AI operations (review, quick rating, chat)
  const pollStatus = useCallback(
    async (reference: string, docId: string, operationType: 'review' | 'quickRating' | 'chat') => {
      let attempts = 0;

      const poll = async () => {
        // Clear any existing timer
        if (pollingTimerRef.current) {
          clearTimeout(pollingTimerRef.current);
          pollingTimerRef.current = null;
        }

        // Check if we've exceeded max attempts
        if (attempts >= MAX_POLLING_ATTEMPTS) {
          if (operationType === 'review') setIsReviewing(false);
          if (operationType === 'quickRating') setIsQuickRating(false);
          if (operationType === 'chat') setIsSendingMessage(false);

          setReviewMessage('Operation timed out. Please try again.');
          addSystemMessage('⚠️ Operation timed out. Please try again.');
          removeReferenceId(docId);
          return;
        }

        try {
          // Get status from API
          const statusData = await apiCall<StatusResponse>(`/status?reference=${reference}`, 'GET');
          if (statusData === undefined) return;

          // Update credit information if available
          if (statusData.status === 'COMPLETE') {
            setSubscriptionSpend(statusData.creditsConsumed);
            setSubscriptionLimit(statusData.creditLimit);
            if ('creditDelta' in statusData) {
              setCreditDelta(statusData.creditDelta!);
            }
          }

          // Handle different status responses
          switch (statusData.status) {
            case 'COMPLETE': {
              if (operationType === 'chat') {
                // For chat, update the loading message with the AI response
                if (statusData.aiResponse) {
                  setChatMessages((prev) => {
                    const updatedMessages = [...prev];
                    // Find the loading message (should be the last one)
                    const loadingIndex = updatedMessages.findIndex((msg) => msg.isLoading);

                    if (loadingIndex !== -1) {
                      // Replace the loading message with the actual response
                      updatedMessages[loadingIndex] = {
                        user: false,
                        msg: statusData.aiResponse!,
                        created: Math.floor(Date.now() / 1000),
                      };
                    }

                    return updatedMessages;
                  });
                }
              } else {
                // For review and quick rating, fetch the full chat history
                const chatResponse = await apiCall<{
                  id: string;
                  messages: ChatMessage[];
                }>('/get-doc/chat', 'POST', { id: docId });

                if (chatResponse) {
                  setChatMessages(chatResponse.messages);
                }
              }

              // Reset operation states
              setIsReviewing(false);
              setIsQuickRating(false);
              setIsSendingMessage(false);

              // Show success message
              setReviewMessage('Operation completed successfully.');
              setTimeout(() => setReviewMessage(null), 10000);

              // Clean up
              removeReferenceId(docId);
              setCreditError(null);
              break;
            }

            case 'ERROR':
            case 'ERROR_NOT_FOUND':
            case 'ERROR_ON_WAIT_LIST': {
              // Remove loading message if this was a chat operation
              if (operationType === 'chat') {
                setChatMessages((prev) => {
                  const updatedMessages = [...prev];
                  const loadingIndex = updatedMessages.findIndex((msg) => msg.isLoading);

                  if (loadingIndex !== -1) {
                    return updatedMessages.filter((_, i) => i !== loadingIndex);
                  }

                  return updatedMessages;
                });
              }

              // Reset operation states
              setIsReviewing(false);
              setIsQuickRating(false);
              setIsSendingMessage(false);

              // Handle specific error types
              if (statusData.status === 'ERROR_NOT_FOUND') {
                const errorMsg = 'Review Required: Perform a review or quick rating before chatting!';
                addSystemMessage(`⚠️ ${errorMsg}`);
                toast({
                  title: 'Review Required',
                  description: 'Perform a review or quick rating before chatting!',
                  duration: 5000,
                });
              } else if (statusData.status === 'ERROR_ON_WAIT_LIST') {
                const errorMsg = 'Sorry, no AI use while on the wait-list...';
                addSystemMessage(`⚠️ ${errorMsg}`);
                toast({
                  title: 'Error',
                  description: errorMsg,
                  variant: 'destructive',
                });
              } else {
                // For general errors, display the error details in the chat
                const errorDetails = statusData.errorDetails || 'An error occurred during the operation.';
                addSystemMessage(`❌ Error: ${errorDetails}`);
                setReviewMessage(errorDetails);
                setTimeout(() => setReviewMessage(null), 10000);
              }

              // Clean up
              removeReferenceId(docId);
              break;
            }

            case 'ERROR_LACK_CREDIT': {
              // Remove loading message if this was a chat operation
              if (operationType === 'chat') {
                setChatMessages((prev) => {
                  const updatedMessages = [...prev];
                  const loadingIndex = updatedMessages.findIndex((msg) => msg.isLoading);

                  if (loadingIndex !== -1) {
                    return updatedMessages.filter((_, i) => i !== loadingIndex);
                  }

                  return updatedMessages;
                });
              }

              // Display credit error
              const creditErrorMsg = `You've exceeded your credit limit. Used: ${statusData.creditsConsumed}, Limit: ${statusData.creditLimit}`;
              addSystemMessage(`❌ ${creditErrorMsg}`);

              // Reset operation states
              setIsReviewing(false);
              setIsQuickRating(false);
              setIsSendingMessage(false);

              // Set credit error
              setCreditError(creditErrorMsg);

              // Clean up
              removeReferenceId(docId);
              break;
            }

            case 'QUEUED':
            case 'IN_PROGRESS': {
              // Continue polling
              attempts++;
              pollingTimerRef.current = setTimeout(poll, POLLING_INTERVAL);
              break;
            }
          }
        } catch (err) {
          console.error('Error polling status:', err);

          // Remove loading message if this was a chat operation
          if (operationType === 'chat') {
            setChatMessages((prev) => {
              const updatedMessages = [...prev];
              const loadingIndex = updatedMessages.findIndex((msg) => msg.isLoading);

              if (loadingIndex !== -1) {
                return updatedMessages.filter((_, i) => i !== loadingIndex);
              }

              return updatedMessages;
            });
          }

          // Reset operation states
          setIsReviewing(false);
          setIsQuickRating(false);
          setIsSendingMessage(false);

          // Display error message
          const errorMsg = 'Error checking operation status. Please try again.';
          addSystemMessage(`❌ ${errorMsg}`);
          setReviewMessage(errorMsg);
          setTimeout(() => setReviewMessage(null), 10000);

          // Clean up
          removeReferenceId(docId);
        }
      };

      // Start polling
      poll();
    },
    [apiCall, setSubscriptionSpend, setSubscriptionLimit, toast, addSystemMessage]
  );

  // Fetch document and chat history on initial load
  useEffect(() => {
    const fetchDocumentAndChat = async () => {
      if (!id) {
        // Handle new document case
        setDocument({
          id: '',
          title: 'Untitled Document',
          context: 'fiction',
          textData: '',
          docOptions: { complete: false },
        });
        setIsLoading(false);
        return;
      }

      try {
        // Fetch document data
        const response = await apiCall<EditDocumentResponse>('/get-doc', 'POST', { id });

        if (response !== undefined) {
          // Set document data
          setDocument(response);
          editorDataRef.current = response.textData;

          // Check for existing reference ID (for in-progress operations)
          const existingReference = getReferenceId(id);
          if (existingReference) {
            setIsReviewing(true);
            pollStatus(existingReference, id, 'review');
          }

          // Fetch chat messages (single call for initial load)
          const chatResponse = await apiCall<{
            id: string;
            messages: ChatMessage[];
          }>('/get-doc/chat', 'POST', { id });

          if (chatResponse) {
            setChatMessages(chatResponse.messages);
          }
        }
      } catch (err) {
        console.error('Error fetching document or chat:', err);
        setError(err instanceof Error ? err : new Error('An error occurred while fetching the document or chat'));
        toast({
          title: 'Error',
          description: 'Error fetching document or chat',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocumentAndChat();
  }, [id, apiCall, pollStatus, toast]);

  // Handle input changes for document fields
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setDocument((prev) => {
      if (!prev) return null;
      if (name === 'complete') {
        return {
          ...prev,
          docOptions: { ...prev.docOptions, complete: value === 'true' },
        };
      }
      return { ...prev, [name]: value };
    });
  };

  // Handle editor content changes
  const handleEditorChange = (_: unknown, editor: { getData: () => string }) => {
    editorDataRef.current = editor.getData();
  };

  // Form data interface
  interface FormData {
    context: string;
    complete: boolean;
  }

  // Handle document save
  const handleSave = async (formData: FormData) => {
    if (!document) return;

    setIsSaving(true);
    setSaveMessage(null);
    setError(null);

    try {
      // Prepare payload
      const payload: EditDocumentRequest = {
        id: document.id || undefined,
        title: document.title,
        textData: editorDataRef.current,
        context: formData.context,
        docOptions: { complete: formData.complete },
      };

      // Save document
      const response = await apiCall<EditDocumentResponse>('/update-doc', 'POST', payload);

      if (response === undefined) return;

      // Update document state
      setDocument((prevDoc) => {
        if (!prevDoc) return response;
        return { ...prevDoc, ...response, id: response.id || prevDoc.id };
      });

      // Show success message
      setSaveMessage('Document saved successfully!');
      toast({
        title: 'Success',
        description: 'Document saved successfully!',
      });

      // Navigate to edit page with ID if this was a new document
      if (!id) {
        navigate(`/edit-doc?id=${response.id}`, { replace: true });
      }
    } catch (err) {
      console.error('Error saving document:', err);

      // Handle different error types
      if (err instanceof Error) {
        if (err.message.includes('400')) {
          setError(new Error('Missing required fields. Please check all fields are filled.'));
          toast({
            title: 'Error',
            description: 'Missing required fields. Please check all fields are filled.',
            variant: 'destructive',
          });
        } else {
          setError(new Error('An error occurred while saving the document. Please try again.'));
          toast({
            title: 'Error',
            description: 'An error occurred while saving the document. Please try again.',
            variant: 'destructive',
          });
        }
      } else {
        setError(new Error('An unknown error occurred. Please try again.'));
        toast({
          title: 'Error',
          description: 'An unknown error occurred. Please try again.',
          variant: 'destructive',
        });
      }

      setSaveMessage('Failed to save document. Please check the error message and try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Update the handleReview function to add aiTask message
  const handleReview = async () => {
    if (!document || !document.id) return;

    // Reset states
    setCreditError(null);
    setIsReviewing(true);
    setReviewMessage(null);
    setError(null);

    // Add aiTask message to chat
    setChatMessages((prev) => [
      ...prev,
      {
        user: true,
        aiTask: true,
        msg: 'Do a full review of my story',
        created: Math.floor(Date.now() / 1000),
      },
    ]);

    // Add loading message
    setChatMessages((prev) => [
      ...prev,
      {
        user: false,
        msg: '',
        isLoading: true,
        created: Math.floor(Date.now() / 1000),
      },
    ]);

    try {
      // Send review request
      const payload: ReviewDocRequest = { id: document.id, offline: true };
      const response = await apiCall<ReviewDocResponse>('/review-doc', 'POST', payload);

      if (!response) return;

      // Store reference ID and start polling
      setReferenceId(response.reference, document.id);
      setReviewMessage(response.message);
      pollStatus(response.reference, document.id, 'review');
    } catch (err) {
      console.error('Error starting review:', err);

      // Remove loading message
      setChatMessages((prev) => {
        const updatedMessages = [...prev];
        const loadingIndex = updatedMessages.findIndex((msg) => msg.isLoading);

        if (loadingIndex !== -1) {
          return updatedMessages.filter((_, i) => i !== loadingIndex);
        }

        return updatedMessages;
      });

      // Handle error
      setIsReviewing(false);
      setError(err instanceof Error ? err : new Error('An error occurred while starting the review'));

      const errorMsg = 'Failed to start review. Please try again.';
      addSystemMessage(`❌ ${errorMsg}`);
      setReviewMessage(errorMsg);
      setTimeout(() => setReviewMessage(null), 10000);

      toast({
        title: 'Error',
        description: errorMsg,
        variant: 'destructive',
      });
    }
  };

  // Update the handleQuickRating function to add aiTask message
  const handleQuickRating = async () => {
    if (!document || !document.id) return;

    // Reset states
    setCreditError(null);
    setIsQuickRating(true);
    setReviewMessage(null);
    setError(null);

    // Add aiTask message to chat
    setChatMessages((prev) => [
      ...prev,
      {
        user: true,
        aiTask: true,
        msg: 'Do a quick rating of my story',
        created: Math.floor(Date.now() / 1000),
      },
    ]);

    // Add loading message
    setChatMessages((prev) => [
      ...prev,
      {
        user: false,
        msg: '',
        isLoading: true,
        created: Math.floor(Date.now() / 1000),
      },
    ]);

    try {
      // Send quick rating request
      const payload: ReviewDocRequest = { id: document.id, offline: true };
      const response = await apiCall<ReviewDocResponse>('/quick-rating', 'POST', payload);

      if (!response) return;

      // Store reference ID and start polling
      setReferenceId(response.reference, document.id);
      setReviewMessage(response.message);
      pollStatus(response.reference, document.id, 'quickRating');
    } catch (err) {
      console.error('Error starting quick rating:', err);

      // Remove loading message
      setChatMessages((prev) => {
        const updatedMessages = [...prev];
        const loadingIndex = updatedMessages.findIndex((msg) => msg.isLoading);

        if (loadingIndex !== -1) {
          return updatedMessages.filter((_, i) => i !== loadingIndex);
        }

        return updatedMessages;
      });

      // Handle error
      setIsQuickRating(false);
      setError(err instanceof Error ? err : new Error('An error occurred while starting the quick rating'));

      const errorMsg = 'Failed to start quick rating. Please try again.';
      addSystemMessage(`❌ ${errorMsg}`);
      setReviewMessage(errorMsg);
      setTimeout(() => setReviewMessage(null), 10000);

      toast({
        title: 'Error',
        description: errorMsg,
        variant: 'destructive',
      });
    }
  };

  // Handle sending a chat message
  const handleSendMessage = async () => {
    if (!document || !document.id || !currentMessage.trim()) return;

    // Set sending state
    setIsSendingMessage(true);
    setReviewMessage(null);
    setError(null);

    // Get message text and clear input
    const messageToSend = currentMessage.trim();
    setCurrentMessage('');

    // Add user message to chat
    setChatMessages((prev) => [
      ...prev,
      {
        user: true,
        msg: messageToSend,
        created: Math.floor(Date.now() / 1000),
      },
    ]);

    // Add loading message
    setChatMessages((prev) => [
      ...prev,
      {
        user: false,
        msg: '',
        isLoading: true,
        created: Math.floor(Date.now() / 1000),
      },
    ]);

    try {
      // Send chat message
      const payload = { id: document.id, message: messageToSend };
      const response = await apiCall<ReviewDocResponse>('/review-doc-chat', 'POST', payload);

      if (!response) return;

      // Store reference ID and start polling
      setReferenceId(response.reference, document.id);
      setReviewMessage(response.message);
      pollStatus(response.reference, document.id, 'chat');
    } catch (err) {
      console.error('Error sending message:', err);

      // Remove loading message
      setChatMessages((prev) => {
        const updatedMessages = [...prev];
        const loadingIndex = updatedMessages.findIndex((msg) => msg.isLoading);

        if (loadingIndex !== -1) {
          return updatedMessages.filter((_, i) => i !== loadingIndex);
        }

        return updatedMessages;
      });

      // Handle error
      setIsSendingMessage(false);
      setError(err instanceof Error ? err : new Error('An error occurred while sending the message'));

      const errorMsg = 'Failed to send message. Please try again.';
      addSystemMessage(`❌ ${errorMsg}`);
      setReviewMessage(errorMsg);
      setTimeout(() => setReviewMessage(null), 10000);

      toast({
        title: 'Error',
        description: errorMsg,
        variant: 'destructive',
      });
    }
  };

  // Handle Enter key in message input
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Toggle chat panel size
  const toggleChatSize = () => {
    setIsChatMaximized(!isChatMaximized);
  };

  // Loading state
  if (isLoading) {
    return <div className="container mx-auto px-4 py-8">Loading...</div>;
  }

  // No document state
  if (!document) {
    return <div className="container mx-auto px-4 py-8">No document found.</div>;
  }

  // Main render
  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSave)}>
        <div id="page-content" className="w-full max-w-full px-6 py-12 max-h-screen overflow-y-auto">
          {/* Error display */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">Error: {error.message}</div>
          )}

          {/* Document form */}
          <div id="formPanel" className="space-y-4">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                name="title"
                value={document.title}
                onChange={handleInputChange}
                placeholder="Enter document title"
              />
            </div>

            {/* Document settings */}
            <Card>
              <CardHeader>
                <CardTitle>Document Settings</CardTitle>
                <CardDescription>Manage your document's properties</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Genre selection */}
                <FormField
                  control={form.control}
                  name="context"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Genre</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value);
                          setDocument((prev) => (prev ? { ...prev, context: value } : null));
                        }}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a genre" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {contextOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                {/* Complete toggle */}
                <FormField
                  control={form.control}
                  name="complete"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Complete</FormLabel>
                        <FormDescription>
                          The document is considered complete, i.e. this is the final version
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={(checked) => {
                            field.onChange(checked);
                            setDocument((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    docOptions: {
                                      ...prev.docOptions,
                                      complete: checked,
                                    },
                                  }
                                : null
                            );
                          }}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Document editor */}
            <div className="space-y-2">
              <Label htmlFor="mainEditor">Content</Label>
              <div className="h-[calc(50vh-10rem)] min-h-[400px] border rounded-md overflow-hidden">
                <Suspense fallback={<div>Loading...</div>}>
                  <Editor id="mainEditor" data={document.textData} onChange={handleEditorChange} />
                </Suspense>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div id="actionsPanel" className="mt-8">
            {/* Credit error display */}
            {creditError && (
              <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                <strong className="font-bold">Credit limit exceeded!</strong>
                <span className="block sm:inline"> {creditError}</span>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4">
              {/* Save button */}
              <Button type="submit" disabled={isSaving}>
                <Save className="mr-2 h-4 w-4" />
                {isSaving ? 'Saving...' : 'Save'}
              </Button>

              {/* Review button */}
              <Button
                onClick={handleReview}
                disabled={isReviewing || isQuickRating || !document.id || !!creditError}
                variant="outline"
              >
                <WandSparkles className="mr-2 h-4 w-4" />
                {isReviewing ? 'Reviewing...' : 'Review'}
              </Button>

              {/* Quick Rating button */}
              <Button
                onClick={handleQuickRating}
                disabled={isReviewing || isQuickRating || !document.id || !!creditError}
                variant="outline"
              >
                <WandSparkles className="mr-2 h-4 w-4" />
                {isQuickRating ? 'Rating...' : 'Quick Rating'}
              </Button>

              {/* Character Chat link */}
              <Button asChild variant="outline" disabled={!document.id}>
                <Link to={`/character-doc-chat?id=${document.id}`} className="inline-flex items-center">
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Character Chat
                </Link>
              </Button>
            </div>

            {/* Status messages */}
            <div className="mt-4">
              {saveMessage && (
                <p className={`text-sm ${saveMessage.includes('Failed') ? 'text-red-600' : 'text-green-600'}`}>
                  {saveMessage}
                </p>
              )}
              {reviewMessage && (
                <p
                  className={`text-sm ${
                    reviewMessage.includes('Error') || reviewMessage.includes('Failed')
                      ? 'text-red-600'
                      : 'text-green-600'
                  }`}
                >
                  {reviewMessage}
                </p>
              )}
            </div>
          </div>

          {/* Chat panel */}
          <div
            id="chatPanel"
            className={`mt-8 ${isChatMaximized ? 'bg-white' : ''}`}
            style={{
              position: isChatMaximized ? 'absolute' : 'relative',
              top: isChatMaximized ? 0 : 'auto',
              left: isChatMaximized ? 0 : 'auto',
              width: isChatMaximized ? '100%' : 'auto',
              height: isChatMaximized ? '100%' : 'auto',
              zIndex: isChatMaximized ? 50 : 'auto',
              ...(isChatMaximized
                ? {
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }
                : {}),
            }}
          >
            {/* Chat header */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Chat Messages</h2>
              <Button variant="ghost" size="sm" onClick={toggleChatSize}>
                {isChatMaximized ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
              </Button>
            </div>

            {/* Chat messages */}
            <ChatMessages messages={chatMessages} />

            {/* Chat input */}
            <div className="mt-4 flex flex-col">
              <div className="flex">
                <Input
                  type="text"
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your message here..."
                  className="flex-grow"
                  disabled={isSendingMessage || !!creditError}
                  maxLength={1000}
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={isSendingMessage || !currentMessage.trim() || !!creditError}
                  className="ml-2"
                >
                  <Send className="mr-2 h-4 w-4" />
                  {isSendingMessage ? 'Sending...' : 'Send'}
                </Button>
              </div>

              {/* Credit usage display */}
              <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                <div>{currentMessage.length}/1000 characters</div>
                {creditDelta !== null && (
                  <div className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-3 w-3 mr-1"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="16" x2="12" y2="12"></line>
                      <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                    AI responses may be inaccurate. Credits used: {creditDelta.toFixed(2)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default EditDocument;
